import { tr } from "../../i18n";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../../auth/AuthContext";
import { Badge, statusTone, Card, fmtDate, fmtDateTime } from "../../components/portal";
import { Field, inputCls } from "../auth/AuthLayout";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
const SERVICE_OPTIONS = [["ERI", "Ethical Readiness Index"], ["INDIVIDUAL", "Single-Entity AI Assessment"], ["GROUP", "Group-Wide AI Assessment"], ["RECERTIFICATION", "Annual ESAIA Recertification"]];
const REQUEST_STATUSES = ["Requested", "Under Review", "Active", "Completed", "Declined"];
export default function AdminClientDetail() {
  const {
    lang = "en",
    id
  } = useParams();
  const [doc, setDoc] = useState(null);
  const [profile, setProfile] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newAss, setNewAss] = useState({
    service_type: "INDIVIDUAL",
    status: "ACTIVE",
    start_date: "",
    completion_date: "",
    notes: ""
  });
  const load = () => api.get(`/admin/clients/${id}`).then(r => {
    setDoc(r.data);
    setProfile({
      first_name: r.data.first_name,
      last_name: r.data.last_name,
      company: r.data.company,
      job_title: r.data.job_title,
      phone: r.data.phone,
      country: r.data.country,
      status: r.data.status || "active"
    });
  }).catch(() => {});
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  if (!doc || !profile) return <p className="text-sm text-esaia-subtle">{tr("Loading...")}</p>;
  const saveProfile = async e => {
    e.preventDefault();
    try {
      await api.patch(`/admin/clients/${id}`, profile);
      toast.success(tr("Client information updated."));
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };
  const createAssessment = async e => {
    e.preventDefault();
    try {
      await api.post(`/admin/clients/${id}/assessments`, newAss);
      toast.success(tr("Assessment created."));
      setAddOpen(false);
      setNewAss({
        service_type: "INDIVIDUAL",
        status: "ACTIVE",
        start_date: "",
        completion_date: "",
        notes: ""
      });
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };
  const setRequestStatus = async (reqId, status) => {
    try {
      await api.patch(`/admin/service-requests/${reqId}`, {
        status
      });
      toast.success(tr("Request status updated."));
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };
  return <div className="mx-auto max-w-6xl" data-testid="admin-client-detail">
      <Link to={`/${lang}/admin/clients`} className="group inline-flex items-center gap-2 text-xs font-semibold text-esaia-blue">
        <ArrowLeft size={14} className="transition-transform group-hover:-translate-x-1" />{" "}{tr("Back to Clients")}{" "}</Link>
      <h1 className="mt-4 text-2xl font-semibold tracking-tight text-esaia-ink">
        {doc.first_name} {doc.last_name}
      </h1>
      <p className="mt-1 text-sm text-esaia-muted">{doc.company || "—"} · {doc.email}</p>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6">
          <Card title={tr("Client Information")} testid="client-info-card">
            <form onSubmit={saveProfile} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Field label={tr("First name")} htmlFor="cf-first"><input id="cf-first" value={profile.first_name} onChange={e => setProfile({
                  ...profile,
                  first_name: e.target.value
                })} className={inputCls} data-testid="client-first-name" /></Field>
                <Field label={tr("Last name")} htmlFor="cf-last"><input id="cf-last" value={profile.last_name} onChange={e => setProfile({
                  ...profile,
                  last_name: e.target.value
                })} className={inputCls} data-testid="client-last-name" /></Field>
              </div>
              <Field label={tr("Company")} htmlFor="cf-company"><input id="cf-company" value={profile.company} onChange={e => setProfile({
                ...profile,
                company: e.target.value
              })} className={inputCls} data-testid="client-company" /></Field>
              <Field label={tr("Job title")} htmlFor="cf-title"><input id="cf-title" value={profile.job_title || ""} onChange={e => setProfile({
                ...profile,
                job_title: e.target.value
              })} className={inputCls} data-testid="client-job-title" /></Field>
              <Field label={tr("Phone")} htmlFor="cf-phone"><input id="cf-phone" value={profile.phone || ""} onChange={e => setProfile({
                ...profile,
                phone: e.target.value
              })} className={inputCls} data-testid="client-phone" /></Field>
              <Field label={tr("Country")} htmlFor="cf-country"><input id="cf-country" value={profile.country || ""} onChange={e => setProfile({
                ...profile,
                country: e.target.value
              })} className={inputCls} data-testid="client-country" /></Field>
              <p className="text-xs text-esaia-subtle">{tr("Account created:")}{" "}{fmtDate(doc.created_at)}</p>
              <button type="submit" className="w-full bg-esaia-blue px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-esaia-dark" data-testid="client-save-button">{tr("Save Changes")}{" "}</button>
            </form>
          </Card>

          <Card title={tr("ERI History")} testid="client-eri-history">
            {doc.eri_submissions.length === 0 ? <p className="text-sm text-esaia-subtle">{tr("No ERI submissions linked to this client.")}</p> : <ul className="divide-y divide-esaia-line">
                {doc.eri_submissions.map(e => <li key={e.id} className="py-3">
                    <Link to={`/${lang}/admin/eri/${e.id}`} className="text-sm font-medium text-esaia-ink hover:text-esaia-blue">{tr("ERI ·")}{" "}{e.total_score}/100
                    </Link>
                    <p className="mt-0.5 font-mono text-[11px] text-esaia-subtle">{fmtDateTime(e.created_at)} · {e.band}</p>
                  </li>)}
              </ul>}
          </Card>

          <Card title={tr("Related Emails")} testid="client-emails">
            {doc.emails.length === 0 ? <p className="text-sm text-esaia-subtle">{tr("No emails linked to this client.")}</p> : <ul className="divide-y divide-esaia-line">
                {doc.emails.map(m => <li key={m.id} className="py-3">
                    <p className="text-sm font-medium text-esaia-ink">{m.subject}</p>
                    <p className="mt-0.5 font-mono text-[11px] text-esaia-subtle">{fmtDateTime(m.received_at)}</p>
                  </li>)}
              </ul>}
          </Card>
        </div>

        <div className="space-y-6 lg:col-span-2">
          <Card title={tr("Assessments")} testid="client-assessments-card" action={<button onClick={() => setAddOpen(true)} data-testid="add-assessment-button" className="inline-flex items-center gap-1.5 bg-esaia-blue px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-esaia-dark">
                <Plus size={14} />{" "}{tr("Add Assessment")}{" "}</button>}>
            {doc.assessments.length === 0 ? <p className="text-sm text-esaia-subtle">{tr("No assessments yet.")}</p> : <div className="overflow-x-auto">
                <table className="w-full min-w-[560px]">
                  <thead>
                    <tr className="border-b border-esaia-line text-left">
                      {["Type", "ID", "Status", "Start", "Completed", "Updated"].map(h => <th key={h} className="pb-3 pr-4 font-mono text-[10px] uppercase tracking-[0.18em] text-esaia-subtle">{tr(h)}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {doc.assessments.map(a => <tr key={a.id} className="border-b border-esaia-line last:border-0">
                        <td className="py-3.5 pr-4 text-sm">
                          <Link to={`/${lang}/admin/assessments/${a.id}`} className="font-semibold text-esaia-ink hover:text-esaia-blue" data-testid={`admin-assessment-link-${a.assessment_id}`}>
                            {tr(SERVICE_OPTIONS.find(([k]) => k === a.service_type)?.[1] || a.service_type)}
                          </Link>
                        </td>
                        <td className="py-3.5 pr-4 font-mono text-[11px] text-esaia-subtle">{a.assessment_id}</td>
                        <td className="py-3.5 pr-4"><Badge tone={statusTone(a.status)}>{tr(a.status)}</Badge></td>
                        <td className="py-3.5 pr-4 text-xs text-esaia-muted">{fmtDate(a.start_date)}</td>
                        <td className="py-3.5 pr-4 text-xs text-esaia-muted">{fmtDate(a.completion_date)}</td>
                        <td className="py-3.5 text-xs text-esaia-muted">{fmtDate(a.updated_at)}</td>
                      </tr>)}
                  </tbody>
                </table>
              </div>}
          </Card>

          <Card title={tr("Service Requests")} testid="client-requests-card">
            {doc.service_requests.length === 0 ? <p className="text-sm text-esaia-subtle">{tr("No service requests.")}</p> : <ul className="divide-y divide-esaia-line">
                {doc.service_requests.map(r => <li key={r.id} className="flex flex-wrap items-center justify-between gap-3 py-3.5" data-testid={`admin-request-${r.id}`}>
                    <div>
                      <p className="text-sm font-medium text-esaia-ink">
                        {tr(SERVICE_OPTIONS.find(([k]) => k === r.service_type)?.[1] || r.service_type)}
                      </p>
                      <p className="mt-0.5 text-xs text-esaia-subtle">{fmtDateTime(r.created_at)}{r.message ? ` · ${r.message}` : ""}</p>
                    </div>
                    <select value={r.status} onChange={e => setRequestStatus(r.id, e.target.value)} data-testid={`request-status-${r.id}`} className="border border-esaia-line bg-white px-3 py-2 text-xs text-esaia-ink focus:border-esaia-blue focus:outline-none">
                      {REQUEST_STATUSES.map(s => <option key={s} value={s}>{tr(s)}</option>)}
                    </select>
                  </li>)}
              </ul>}
          </Card>
        </div>
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md" data-testid="add-assessment-dialog">
          <DialogHeader>
            <DialogTitle className="text-esaia-ink">{tr("Add Assessment")}</DialogTitle>
            <DialogDescription className="text-esaia-muted">{tr("Create a new assessment for")}{" "}{doc.company || `${doc.first_name} ${doc.last_name}`}.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={createAssessment} className="space-y-4">
            <Field label={tr("Assessment type")} required htmlFor="na-type">
              <select id="na-type" value={newAss.service_type} onChange={e => setNewAss({
              ...newAss,
              service_type: e.target.value
            })} className={inputCls} data-testid="na-type">
                {SERVICE_OPTIONS.map(([k, label]) => <option key={k} value={k}>{tr(label)}</option>)}
              </select>
            </Field>
            <Field label={tr("Status")} required htmlFor="na-status">
              <select id="na-status" value={newAss.status} onChange={e => setNewAss({
              ...newAss,
              status: e.target.value
            })} className={inputCls} data-testid="na-status">
                {["PENDING", "ACTIVE", "COMPLETED"].map(s => <option key={s} value={s}>{tr(s)}</option>)}
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label={tr("Start date")} htmlFor="na-start">
                <input id="na-start" type="date" value={newAss.start_date} onChange={e => setNewAss({
                ...newAss,
                start_date: e.target.value
              })} className={inputCls} data-testid="na-start" />
              </Field>
              <Field label={tr("Target completion")} htmlFor="na-end">
                <input id="na-end" type="date" value={newAss.completion_date} onChange={e => setNewAss({
                ...newAss,
                completion_date: e.target.value
              })} className={inputCls} data-testid="na-end" />
              </Field>
            </div>
            <Field label={tr("Internal notes")} htmlFor="na-notes">
              <textarea id="na-notes" rows={3} value={newAss.notes} onChange={e => setNewAss({
              ...newAss,
              notes: e.target.value
            })} className={inputCls} data-testid="na-notes" />
            </Field>
            <button type="submit" className="w-full bg-esaia-blue px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-esaia-dark" data-testid="na-submit">{tr("Create Assessment")}{" "}</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>;
}
